import { useQuery } from "@tanstack/react-query"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Send } from "lucide-react"
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { supabase } from "@/integrations/supabase/client"
import { useToast } from "@/hooks/use-toast"
import { useState, useEffect } from "react"
import { GenerateCodeForm } from "@/components/generate-code-form"
import { useNavigate } from "react-router-dom"
import { AppLayout } from "@/components/app-layout"
import { formatInTimeZone } from 'date-fns-tz'
import { fr } from 'date-fns/locale'

const IndexContent = () => {
  const { toast } = useToast()
  const navigate = useNavigate()
  const [selectedKey, setSelectedKey] = useState<{ id: string; name: string } | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session }, error } = await supabase.auth.getSession()
      
      if (error || !session) {
        console.error("Authentication error:", error)
        toast({
          title: "Authentification requise",
          description: "Veuillez vous connecter pour accéder à cette page",
          variant: "destructive",
        })
        navigate("/login")
        return
      }
      setIsLoading(false)
    }

    checkAuth()
  }, [navigate, toast])

  const { data: keysResponse, error } = useQuery({
    queryKey: ['keynest-keys'],
    queryFn: async () => {
      console.log("Fetching keynest keys from Edge Function...")
      const { data, error } = await supabase.functions.invoke('get-keynest-keys', {
        method: 'GET'
      })
      
      if (error) {
        console.error("Error fetching keys:", error)
        throw error
      }

      console.log("Keys fetched successfully:", data)
      const keys = data?.ResponsePacket?.KeyList || []
      return keys.map(key => ({
        id: key.KeyId,
        name: key.KeyName,
        status: key.StatusType === "In Locker" ? "available" : "unavailable",
        lastMovement: key.LastMovement
      }))
    },
    enabled: !isLoading
  })

  if (isLoading) {
    return <div>Chargement...</div>
  }

  if (error) {
    return <div>Erreur lors du chargement des clés</div>
  }

  const formatDateCayenne = (date: string | null) => {
    if (!date) return "-"; // Retourne un tiret si la date est null
    
    // Ensure the date is treated as UTC by appending 'Z' if it's not already present
    const utcDate = date.endsWith('Z') ? date : `${date}Z`;
    
    try {
      return formatInTimeZone(
        new Date(utcDate),
        'America/Cayenne',
        "dd/MM/yyyy HH:mm",
        { locale: fr }
      );
    } catch (error) {
      console.error("Error formatting date:", error);
      return "-"; // En cas d'erreur de formatage
    }
  }

  return (
    <AppLayout title="Gestion des clés">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nom</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Dernier mouvement</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {keysResponse?.map((key) => (
              <TableRow key={key.id}>
                <TableCell className="font-medium">{key.name}</TableCell>
                <TableCell>
                  <Badge 
                    variant={key.status === "available" ? "default" : "destructive"}
                    className={key.status === "available" ? "bg-green-500 hover:bg-green-600" : ""}
                  >
                    {key.status === "available" ? "Disponible" : "Indisponible"}
                  </Badge>
                </TableCell>
                <TableCell>{formatDateCayenne(key.lastMovement)}</TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedKey({ id: key.id, name: key.name })}
                  >
                    <Send className="mr-2 h-4 w-4" />
                    Générer un code
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="mt-4">
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious href="#" />
            </PaginationItem>
            <PaginationItem>
              <PaginationLink href="#">1</PaginationLink>
            </PaginationItem>
            <PaginationItem>
              <PaginationNext href="#" />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>

      <Dialog open={selectedKey !== null} onOpenChange={(open) => !open && setSelectedKey(null)}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Générer un code pour {selectedKey?.name}</DialogTitle>
          </DialogHeader>
          {selectedKey && (
            <GenerateCodeForm
              keyId={selectedKey.id}
              keyName={selectedKey.name}
              onSuccess={() => setSelectedKey(null)}
              onCancel={() => setSelectedKey(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  )
}

const Index = () => {
  return <IndexContent />
}

export default Index